import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Scale, Menu, X, Moon, Sun } from "lucide-react";
import LoginMenu from "./LoginMenu";
import { useTheme } from "../context/ThemeContext";
import { motion, AnimatePresence } from "framer-motion";
import { AUDIENCES } from "../utils/audience";
import useHasDirectoryListing from "../hooks/useHasDirectoryListing";
import "./Header.css";

/** The attorney landing page. Not "/" — that is the audience splitter. */
const ATTORNEY_HOME = AUDIENCES.attorney.path;

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isAdmin, logout, user } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Someone already in the directory is offered their listing, not an
  // invitation to join it.
  const hasDirectoryListing = useHasDirectoryListing();
  const directoryTo = hasDirectoryListing ? "/my-listing" : "/directory";
  const directoryLabel = hasDirectoryListing
    ? "My Directory Listing"
    : "Join Free Directory";

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const handleDirectoryClick = () => {
    closeMenu();
    window.scrollTo(0, 0);
  };

  // Attorney Reviews is an anchor on the attorney landing page. That page moved
  // from "/" to "/attorneys" when the audience splitter became the homepage, so
  // both the in-page scroll and the cross-page navigation target /attorneys —
  // pointing either at "/" lands on the splitter, which has no testimonials.
  const handleTestimonialsClick = (e) => {
    e.preventDefault();
    closeMenu();
    if (location.pathname === ATTORNEY_HOME) {
      // On mobile the drawer locks the page (body is position:fixed) and its
      // cleanup restores the previous scroll offset. Scrolling synchronously
      // here does nothing, and then that cleanup snaps back to where the
      // visitor was — which is why the link looked dead on a phone and only
      // worked after leaving the page and coming back. Defer past the unlock.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = document.getElementById("testimonials");
          if (el) {
            el.scrollIntoView({ behavior: "smooth" });
          }
        });
      });
    } else {
      navigate(`${ATTORNEY_HOME}?scrollTo=testimonials`);
    }
  };

  useEffect(() => {
    if (!isMenuOpen) return;

    const scrollY = window.scrollY;
    const body = document.body;
    const html = document.documentElement;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
      htmlOverflow: html.style.overflow,
      htmlOverscroll: html.style.overscrollBehavior,
    };

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    html.style.overscrollBehavior = "none";

    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      html.style.overflow = prev.htmlOverflow;
      html.style.overscrollBehavior = prev.htmlOverscroll;
      window.scrollTo(0, scrollY);
    };
  }, [isMenuOpen]);

  // Function to check if a path is active
  const isActive = (path) => {
    const { pathname } = location;

    if (path === "/") {
      return pathname === "/";
    }
    // For dashboard/Research, check if pathname starts with /dashboard or is /login
    if (path === "/research") {
      return pathname.startsWith("/research") || pathname === "/login";
    }
    // For admin, check if pathname starts with /admin
    if (path === "/admin") {
      return pathname.startsWith("/admin");
    }
    // For debate, check /debate, /oldDebate, /debate-mode
    if (path === "/debate") {
      return (
        pathname === "/debate" ||
        pathname === "/oldDebate" ||
        pathname.startsWith("/debate-mode")
      );
    }
    // For other paths, check exact match
    return pathname === path;
  };

  // Get active class name
  const getActiveClass = (path) => {
    return isActive(path) ? "header-nav-link-active" : "";
  };

  return (
    <div className="header-anchor">
    <div className="header-wrapper landing-header-wrapper">
      <header className="header-navbar landing-navbar">
        <div className="header-navbar-content landing-navbar-content">
          <Link to="/" className="header-logo landing-logo" onClick={closeMenu}>
            <img
              src="/logo.png"
              alt="LawFusion"
              className="header-logo-img landing-logo-img"
            />
          </Link>

          {!isMenuOpen && (
            <button
              className="header-mobile-toggle"
              onClick={toggleMenu}
              aria-label="Toggle menu"
            >
              <Menu size={24} />
            </button>
          )}

          <AnimatePresence>
            {isMenuOpen && (
              <>
                {/* Backdrop */}
                <motion.div
                  className="header-mobile-backdrop"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={closeMenu}
                />

                {/* Side Drawer */}
                <motion.nav
                  className="header-nav-links mobile-drawer-open"
                  initial={{ x: "100%" }}
                  animate={{ x: 0 }}
                  exit={{ x: "100%" }}
                  transition={{ type: "spring", damping: 25, stiffness: 200 }}
                >
                  <div className="drawer-header">
                    <Link to="/" className="header-logo" onClick={closeMenu}>
                      <img
                        src="/logo.png"
                        alt="LawFusion"
                        className="header-logo-img"
                      />
                    </Link>
                    <button className="drawer-close-btn" onClick={closeMenu}>
                      <X size={24} />
                    </button>
                  </div>

                  <div className="drawer-content">
                    <Link
                      to="/research"
                      className={getActiveClass("/research")}
                      onClick={closeMenu}
                    >
                      Research Mode
                    </Link>
                    <Link
                      to="/debate"
                      className={`header-nav-link-debate ${getActiveClass("/debate")}`}
                      onClick={closeMenu}
                    >
                      <span>Debate Mode</span>
                      <Scale size={16} style={{ marginLeft: "0.5rem" }} />
                    </Link>
                    <a
                      href={`${ATTORNEY_HOME}#testimonials`}
                      className={location.pathname === ATTORNEY_HOME && window.location.hash === "#testimonials" ? "header-nav-link-active" : ""}
                      onClick={handleTestimonialsClick}
                      style={{ cursor: "pointer" }}
                    >
                      Attorney Reviews
                    </a>
                    <Link
                      to="/pricing"
                      className={getActiveClass("/pricing")}
                      onClick={closeMenu}
                    >
                      Pricing
                    </Link>
                    <Link
                      to={directoryTo}
                      className={getActiveClass(directoryTo)}
                      onClick={handleDirectoryClick}
                    >
                      {directoryLabel}
                    </Link>
                    <Link
                      to="/settings"
                      className={getActiveClass("/settings")}
                      onClick={closeMenu}
                    >
                      Settings
                    </Link>
                    <button
                      type="button"
                      className="header-nav-button drawer-theme-toggle"
                      onClick={toggleDarkMode}
                      aria-label={`Switch to ${darkMode ? "light" : "dark"} mode`}
                    >
                      <span>{darkMode ? "Light Mode" : "Dark Mode"}</span>
                      {darkMode ? <Sun size={16} /> : <Moon size={16} />}
                    </button>
                    {isAuthenticated ? (
                      <>
                        {isAdmin && (
                          <Link
                            to="/admin"
                            className={getActiveClass("/admin")}
                            onClick={closeMenu}
                          >
                            Admin
                          </Link>
                        )}
                        <button
                          type="button"
                          onClick={async () => {
                            await logout();
                            closeMenu();
                            navigate("/login", { replace: true });
                          }}
                          className="header-nav-button"
                        >
                          Logout
                        </button>
                      </>
                    ) : (
                      <>
                        <Link
                          to="/login"
                          className={getActiveClass("/login")}
                          onClick={closeMenu}
                        >
                          Log in to LawFusion
                        </Link>
                        <Link
                          to="/login?area=directory"
                          className={getActiveClass("/login")}
                          onClick={closeMenu}
                        >
                          Log in to Directory
                        </Link>
                      </>
                    )}
                  </div>

                  {!user?.subscriptionStatus || user?.subscriptionStatus === "inactive" ? (
                    !user?.hasUsedTrial && (
                      <div className="drawer-footer">
                        <button
                          type="button"
                          className="header-cta-btn"
                          onClick={() => {
                            closeMenu();
                            navigate("/pricing");
                          }}
                        >
                          Start Free Trial
                        </button>
                      </div>
                    )
                  ) : null}
                </motion.nav>
              </>
            )}
          </AnimatePresence>

          {/* Desktop Navigation */}
          <nav className="header-nav-links desktop-nav">
            <Link to="/research" className={getActiveClass("/research")}>
              Research Mode
            </Link>
            <Link
              to="/debate"
              className={`header-nav-link-debate ${getActiveClass("/debate")}`}
            >
              <span>Debate Mode</span>
              <Scale size={16} />
            </Link>
            <a
              href={`${ATTORNEY_HOME}#testimonials`}
              className={location.pathname === ATTORNEY_HOME && window.location.hash === "#testimonials" ? "header-nav-link-active" : ""}
              onClick={handleTestimonialsClick}
              style={{ cursor: "pointer" }}
            >
              Attorney Reviews
            </a>
            <Link to="/pricing" className={getActiveClass("/pricing")}>
              Pricing
            </Link>
            <Link
              to={directoryTo}
              className={getActiveClass(directoryTo)}
              onClick={handleDirectoryClick}
            >
              {directoryLabel}
            </Link>
            {isAuthenticated ? (
              <>
                {isAdmin && (
                  <Link to="/admin" className={getActiveClass("/admin")}>
                    Admin
                  </Link>
                )}
                <button
                  type="button"
                  onClick={async () => {
                    await logout();
                    navigate("/login", { replace: true });
                  }}
                  className="header-nav-button landing-nav-button"
                >
                  Logout
                </button>
              </>
            ) : (
              <LoginMenu className={getActiveClass("/login")} />
            )}
            {(!user?.subscriptionStatus || user?.subscriptionStatus === "inactive") && !user?.hasUsedTrial && (
              <button
                type="button"
                className="header-cta-btn landing-cta-btn"
                onClick={() => navigate("/pricing")}
              >
                Start Free Trial
              </button>
            )}
          </nav>
        </div>
      </header>
    </div>
    </div>
  );
};

export default Header;
